import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";

import {
  Stack,
  TextField,
  CardActions,
  CardContent,
  Typography,
  Modal,
  Card,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from "@mui/material";

import courtesyService from "../services/CourtesyService";
import ClientDialog from "../../clients/components/ClientDialog";
import CourtesyDialog from "./CourtesyDialog";

interface ModalProps {
  title: string;
  open: boolean;
  handler: () => void;
  setError: () => void;
  setSuccess: () => void;
  confirmButtonText: string;
}

export default function CourtesyModal(props: ModalProps) {
  const [isSubmitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [lens, setLens] = useState([]);
  const [obs, setObs] = useState("");
  const [selectedClient, setSelectedClient]: any = useState(null); // Estado do cliente selecionado
  const [selectedLens, setSelectedLens]: any = useState(null); // Estado do cliente selecionado
  const [numCortesias, setNumCortesias]: any = useState(null);
  const [type, setType]: any = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    courtesyService
      .getClients()
      .then((data: any) => setClients(data.data))
      .catch((error: any) => console.error(error));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedClient) {
      courtesyService
        .getClientsLens(selectedClient)
        .then((data: any) => setLens(data.data))
        .catch((error: any) => console.error(error));
    } else {
      setLens([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  const handleSubmit = async () => {
    try {
      const data = {
        id_Customer: selectedClient.id_Customer,
        idLens_Order: selectedLens.id_Lens,
        type: type,
        numCortesias: numCortesias,
        observation: obs,
      };
      await courtesyService
        .insert(data)
        .then((data: any) => {
          setIsOpen(false);
          props.handler();
          props.setSuccess();
          setSelectedClient(null);
          setSelectedLens(null);
          setType(null);
          setNumCortesias(null);
        })
        .catch((error: any) => props.setError());
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <CourtesyDialog
        open={isOpen}
        title="Adicionar cortesia"
        description="Tem certeza que deseja adicionar essa cortesia?"
        cancelButtonText="Cancelar"
        confirmButtonText="Adicionar"
        action={() => handleSubmit()}
        handler={() => setIsOpen(false)}
      />
      <Modal
        open={props.open}
        onClose={() => {
          props.handler();
        }}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Card sx={{ minWidth: "30vw", maxHeight: "80vh", overflowY: "auto" }}>
          <CardContent>
            <Typography
              variant="h4"
              marginBottom={4}
              color="#004A78"
              gutterBottom
            >
              Adicionar Cortesia
            </Typography>
            <Stack spacing={1} marginTop={3}>
              <Typography variant="h6" component="h2">
                Cliente
              </Typography>
              <Autocomplete
                disablePortal
                options={clients}
                getOptionLabel={(option: any) =>
                  `${option.initials_Customer} - ${option.name_Customer}`
                }
                sx={{ width: 380, ml: 2 }}
                onChange={(event, value) => setSelectedClient(value)} // Atualiza o estado
                renderInput={(params) => (
                  <TextField {...params} label="Nome do cliente" />
                )}
              />
            </Stack>

            <Stack spacing={1} marginTop={3}>
              <Typography variant="h6" component="h2">
                Tratamento
              </Typography>
              <Autocomplete
                disablePortal
                options={lens}
                getOptionLabel={(option: any) => option.name_Lens}
                sx={{ width: 380, ml: 2 }}
                onChange={(event, value) => setSelectedLens(value)} // Atualiza o estado
                renderInput={(params) => (
                  <TextField {...params} label="Nome do tratamento" />
                )}
              />
            </Stack>
            <Stack spacing={1} marginTop={3}>
              <Typography variant="h6" component="h2">
                Tipo
              </Typography>
              <FormControl fullWidth>
                <InputLabel color="secondary" id="amount">
                  Cortesia/reembolso*
                </InputLabel>
                <Select
                  fullWidth
                  labelId="amount"
                  color="secondary"
                  label="Cortesia/reembolso*"
                  onChange={(e) => setType(e.target.value)} // Atualiza o estado
                >
                  <MenuItem value="cortesia">Cortesia</MenuItem>
                  <MenuItem value="reembolso">Reembolso</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack spacing={1} marginTop={3}>
              <Typography variant="h6" component="h2">
                Quantidade de cortesias
              </Typography>
              <TextField
                type="number"
                fullWidth
                color="secondary"
                variant="outlined"
                placeholder="Digite aqui a quantidade de cortesias..."
                inputProps={{ min: 0, step: 1 }} // não permite negativos nem decimais
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^[0-9]+$/.test(value)) {
                    setNumCortesias(value);
                  }
                }}
              />
            </Stack>
            <Stack spacing={1} marginTop={3}>
              <Typography variant="h6" component="h2">
                Observações
              </Typography>
              <TextField
                type="text"
                fullWidth={true}
                value={obs}
                color="secondary"
                variant="outlined"
                placeholder="Digite aqui a observação..."
                onChange={(e) => setObs(e.target.value)} // Atualiza o estado
              />
            </Stack>
          </CardContent>
          <CardActions>
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              onClick={() => setIsOpen(true)}
              disabled={
                selectedClient === null ||
                selectedLens === null ||
                numCortesias === null ||
                type === null
              }
              loading={isSubmitting}
              sx={{
                m: 1,
                height: "48px",
                borderRadius: "30px",
                backgroundColor: "#CDCDCD",
                color: "#004A78",
                "&:hover": {
                  bgcolor: "#004A78",
                  color: "#FEFEFE",
                },
              }}
              autoFocus
            >
              {props.confirmButtonText}
            </LoadingButton>
          </CardActions>
        </Card>
      </Modal>
    </>
  );
}
